import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux';
import Classes from './RecordTable.module.css'
import useScreenWidth from '../../../../../../Hooks/useScreenWidth';
import SkeletonLoading from '../../../../../GlobalComponents/SkeletonLoading/SkeletonLoading';
import { Pagination, Stack } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import TableRow from './TableRow';
import { useNavigate } from 'react-router-dom';
import { AddIcon } from '../../../../../../utils/Icons';
import AddMemberPopup from '../Popups/AddMemberPopup/AddMemberPopup';
import BasicNotifyPopup from '../Popups/BasicNotifyPopup/BasicNotifyPopup';
import EmailSuccessPopup from '../Popups/EmailSuccessPopup/EmailSuccessPopup';
import backend from '../../../../../../api';

export default function RecordTable() {
  const [loading, setLoading] = useState(true);
  const navigateTo = useNavigate();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= 480;

  const onBoarding = useSelector((state) => state.companyInfo.info);
  const { user } = useSelector((state) => state.auth);
  const companyId = onBoarding?._id;
  const userID = user?._id;

  const [allInvites, setAllInvites] = useState([]);
  const [filteredInvites, setFilteredInvites] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const [decision, setDecision] = useState('Admin');
  const [inviteeEmail, setInviteeEmail] = useState('');
  const [openEmailSentPopup, setOpenEmailSentPopup] = useState(false);
  const [openAlreadyExistPopup, setOpenAlreadyExistPopup] = useState(false);
  const [pending, setIsPending] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);

  // ✅ NEW: totals map fetched from same endpoint used in Routes module
  // { [employeeUserId]: { totalBookings, totalEarnings } }
  const [totalsMap, setTotalsMap] = useState({});

  const handleCloseEmailSentPopup = () => setOpenEmailSentPopup(false);
  const handleCloseAlreadyExistPopup = () => setOpenAlreadyExistPopup(false);

  useEffect(() => {
    if (companyId) getAllInvites();
  }, [companyId]);

  // ✅ NEW: fetch sales & earnings from board (same as Routes)
  useEffect(() => {
    if (!companyId) return;
    let alive = true;

    (async () => {
      try {
        const { data } = await backend.get('/api/bookings/board', { params: { companyId } });
        if (!alive) return;

        if (data?.ok) {
          const map = {};
          (data?.employees || []).forEach((col) => {
            // ignore unassigned column
            if (!col?.id || col.id === 'unassigned') return;
            map[col.id] = {
              totalBookings: Number(col?.totalBookings ?? 0),
              totalEarnings: Number(col?.totalEarnings ?? 0),
            };
          });
          setTotalsMap(map);
        }
      } catch (e) {
        console.error('board fetch (team totals) error:', e);
      }
    })();

    return () => { alive = false; };
  }, [companyId]);

  const getAllInvites = async () => {
    try {
      const response = await backend.get('/api/invite/');
      if (response && Array.isArray(response.data)) {
        const companyInvites = response.data.filter((invite) => invite.companyId === companyId);
        const activeInvites = companyInvites.filter((invite) => invite.status === true);
        setAllInvites(companyInvites);
        setFilteredInvites(activeInvites);
      }
    } catch (error) {
      console.error('Error fetching invites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    if (value === '') {
      setFilteredInvites(allInvites.filter((invite) => invite.status === true));
    } else {
      const filtered = allInvites.filter((invite) => {
        const name = invite.isAccepted && invite.userId?.name ? invite.userId.name.toLowerCase() : 'pending';
        const email = invite.inviteeEmail ? invite.inviteeEmail.toLowerCase() : '';
        return name.includes(value) || email.includes(value);
      });
      setFilteredInvites(filtered);
    }
  };

  const mapInviteToTeamMember = (invite) => {
    let roleText, backendRole;

    if (invite.isAccepted && invite.userId) {
      switch (invite.userId.role) {
        case 'company' || 'Admin':
          roleText = 'Admin' || 'company';
          backendRole = 'company';
          break;
        case 'employee' || 'Employee':
          roleText = 'employee' || 'Employee';
          backendRole = 'employee';
          break;
        case 'customer':
          roleText = 'Client';
          backendRole = 'customer';
          break;
        default:
          roleText = 'Unknown';
          backendRole = '';
      }

      return {
        id: invite._id,
        userId: invite.userId._id,
        name: invite.userId.name,
        email: invite.userId.email,
        role: roleText,
        backendRole: backendRole,
        status: 'Active',
      };
    } else {
      switch (invite.role) {
        case 'company' || 'Admin':
          roleText = 'Admin' || 'company';
          backendRole = 'company';
          break;
        case 'employee' || 'Employee':
          roleText = 'employee' || 'Employee';
          backendRole = 'employee';
          break;
        case 'customer':
          roleText = 'Client';
          backendRole = 'customer';
          break;
        default:
          roleText = 'Unknown';
          backendRole = '';
      }

      return {
        id: invite._id,
        name: 'Pending',
        email: invite.inviteeEmail,
        role: roleText,
        backendRole: backendRole,
        status: 'Pending Invitation',
      };
    }
  };

  // ✅ NEW: merge sales + earnings into each row
  const teamMembers = filteredInvites.map((invite) => {
    const base = mapInviteToTeamMember(invite);

    const totals = base?.userId ? totalsMap?.[base.userId] : null;
    const sales = Number(totals?.totalBookings ?? 0);
    const earnings = Number(totals?.totalEarnings ?? 0);

    return { ...base, sales, earnings };
  });

  const totalItems = teamMembers.length;
  const currentItems = teamMembers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  function handlePageChange(e, pageNum) {
    setCurrentPage(pageNum);
  }

  const createInvite = async () => {
    setInviteLoading(true);
    const matchingInvite = allInvites.find((invite) => invite.inviteeEmail === inviteeEmail);
    let role, companySubAdmin;

    switch (decision) {
      case 'Admin':
        role = 'company';
        companySubAdmin = true;
        break;
      case 'Employee':
        role = 'employee';
        companySubAdmin = false;
        break;
      default:
        console.error('Invalid role selected');
        return;
    }

    let userExists = false;
    let existingUserId;
    let existingInviteId;

    if (matchingInvite) {
      if (matchingInvite.status === true) {
        setIsPending(!matchingInvite.isAccepted);
        setOpenAlreadyExistPopup(true);
        setInviteLoading(false);
        return;
      } else {
        const body = { status: true, role: role };
        userExists = true;
        existingInviteId = matchingInvite._id;
        existingUserId = matchingInvite.userId?._id;
        try {
          await Promise.all([
            backend.put(`/api/invite/${existingInviteId}`, body),
            backend.put(`/api/user/${existingUserId}`, { role: role, companySubAdmin: companySubAdmin }),
          ]);
          sendEmail(existingInviteId, decision, role, matchingInvite.inviteeEmail, true, existingUserId);
          setInviteLoading(false);
        } catch (error) {
          console.error('Error updating invite:', error);
        }
        return;
      }
    }

    const body = {
      senderId: userID,
      inviteeEmail: inviteeEmail,
      companyId: companyId,
      role: role,
    };

    try {
      const response = await backend.post('/api/invite', body);
      if (response) {
        sendEmail(response.data._id, decision, role, inviteeEmail, false);
        setOpenInvitePopup(false);
        setInviteLoading(false);
        setOpenEmailSentPopup();
        getAllInvites();
      }
    } catch (error) {
      console.error('Error creating invite:', error);
    }
  };

  const getHashCode = (roleText) => {
    switch (roleText) {
      case 'Admin' || 'company':
        return 'x4a679ffdfdfqAEF5';
      case 'Employee' || 'employee':
        return 'managerHash123';
      case 'Client':
        return 'clientHash456';
      default:
        return '';
    }
  };

  const sendEmail = async (inviteId, roleText, backendRole, inviteeEmail, userExists = false, existingUserId = null) => {
    const hashCode = getHashCode(roleText);
    const existingUserHashCode = 'asasaderuxrtdfqE31xB';

    let link;
    if (!userExists) {
      link = `https://fuelclubs.com/${hashCode}/${userID}/${inviteId}/${companyId}`;
    } else {
      link = `https://fuelclubs.com/${existingUserHashCode}/${existingUserId}/${inviteId}/${companyId}`;
    }

    const body = {
      email: inviteeEmail,
      companyName: 'fuelClubs',
      link: link,
      role: roleText,
      userExists: userExists,
    };

    try {
      await backend.post('/email/invite/', body);
      setOpenInvitePopup(false);
      setOpenEmailSentPopup(true);
      getAllInvites();
    } catch (error) {
      console.error('Error sending email:', error);
    }
  };

  const [openInvitePopup, setOpenInvitePopup] = useState(false);
  const handleOpenInvitePopup = () => setOpenInvitePopup(true);
  const handleCloseInvitePopup = () => setOpenInvitePopup(false);

  const companyCount = currentItems.filter(item => item.role === "company").length || 0;
  const employeeCount = currentItems.filter(item => item.role === "employee").length || 0;

  return (
    <>
      <div className={Classes.header}>
        <div>
          <h3 className={Classes.title3} >Team</h3>
          <h5 className={Classes.text} >Invite and manage your team members.</h5>
        </div>
        <div>
          <div className={Classes.searcCon}>
            <img src="/assets/images/search.png" alt="icon" />
            <input type="text" placeholder="Search..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </div>
      </div>

      <div className={Classes.hero}>
        <div className={Classes.table} >
          <div className={Classes.tableWrapper}>

            <div className={Classes.tableBanner} >
              <div className='flexAlign10' >
                <div className={Classes.tableBannerItem}>
                  <h5>Admins</h5>
                  <h4>{companyCount}</h4>
                </div>
                <div className={Classes.tableBannerItem}>
                  <h5>Employees</h5>
                  <h4>{employeeCount}</h4>
                </div>
                <div className={Classes.tableBannerItem}>
                  <h5 className={Classes.allText} >All</h5>
                  <h4>{employeeCount + companyCount}</h4>
                </div>
              </div>
              <div>
                <button className={Classes.addBtn} onClick={handleOpenInvitePopup} >
                  <AddIcon height='12px' fill='white' />
                  <h5>Add team member</h5>
                </button>
              </div>
            </div>

            {/* ✅ UPDATED HEADER: added Sales + Earnings */}
            <div className={Classes.tableHeader}>
              <div className={`${Classes.headerItem} ${Classes.header1}`}>
                <h5>Name</h5>
              </div>
              <div className={`${Classes.headerItem} ${Classes.header2}`}>
                <h5>Email</h5>
              </div>
              <div className={`${Classes.headerItem} ${Classes.header3}`}>
                <h5>Role</h5>
              </div>
              <div className={`${Classes.headerItem} ${Classes.header4}`}>
                <h5>Sales</h5>
              </div>
              <div className={`${Classes.headerItem} ${Classes.header5}`}>
                <h5>Earnings</h5>
              </div>
              <div className={`${Classes.headerItem} ${Classes.header6}`}>
                <h5>Status</h5>
              </div>
              <div className={`${Classes.headerItem} ${Classes.header7}`}>
              </div>
            </div>

            <div className={Classes.tableBody}>
              {loading ? (
                <SkeletonLoading
                  length={itemsPerPage}
                  height={isMobile ? '3.5rem' : '2.8rem'}
                  width="100%"
                  br="6px"
                  padding='0'
                />
              ) : currentItems && currentItems.length > 0 ? (
                currentItems.map((cust, idx) => (
                  <TableRow
                    key={cust.id}
                    value={cust}
                    index={idx}
                    sendEmail={sendEmail}
                    getAllInvites={getAllInvites}
                  />
                ))
              ) : (
                <h5 className='placeholderText' style={{ margin: '15px 19px' }} >No team yet...</h5>
              )}
            </div>

            {currentItems && currentItems.length > 0 &&
              <div className={Classes.pagFooter}>
                <h5 style={{ height: '1.2rem' }}>
                  {loading
                    ? <SkeletonLoading height='1.2rem' width='10rem' br='4px' padding='0' />
                    : `Showing ${startItem} to ${endItem} of ${totalItems} results`
                  }
                </h5>
                <Stack spacing={2} className={Classes.pagination}>
                  {loading
                    ? <SkeletonLoading height='1.2rem' width='5rem' br='4px' padding='0' />
                    : <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      shape="rounded"
                    />
                  }
                </Stack>
              </div>
            }
          </div>
        </div>
      </div>

      <AnimatePresence>
        {openInvitePopup && (
          <AddMemberPopup
            handleClose={handleCloseInvitePopup}
            onInvite={createInvite}
            decision={decision}
            setdecision={setDecision}
            inviteeEmail={inviteeEmail}
            setInviteeEmail={setInviteeEmail}
            handleOpenInvitePopup={handleOpenInvitePopup}
            inviteLoading={inviteLoading}
          />
        )}
      </AnimatePresence>

      <AnimatePresence exit="wait">
        {openEmailSentPopup && (
          <EmailSuccessPopup
            closePopup={handleCloseEmailSentPopup}
            title="WOOHOO!"
            description="Your invite was sent successfully."
            buttonText="Continue"
          />
        )}
      </AnimatePresence>

      <AnimatePresence exit="wait">
        {openAlreadyExistPopup && (
          <BasicNotifyPopup
            closePopup={handleCloseAlreadyExistPopup}
            title="Invitation Already Exists"
            description={pending ? "There is already a pending invitation for this user." : "There is already an active user on this email."}
            buttonText="Return"
            icon="exclamation"
          />
        )}
      </AnimatePresence>
    </>
  )
}
